import type { Shape } from "@gettactic/map-builder/src/types";
import {
  computeNeighborDistances,
  getGroupSnapPosition,
  getSnapPosition
} from "@gettactic/map-builder/src/utils/snap";
import { getShapeAabb, unionRects } from "../document/bounds";
import type { SnapGuides } from "../render/overlayChannel";
import type { EditorStore } from "../store/createEditorStore";
import type { Point, Rect } from "../types";
import { type Box, snapEqualSpacing } from "./spacingSnap";
import { snapToWallFaces, wallFaces } from "./wallFlushSnap";

/** Same radius `snap.ts` prunes candidates with, in world units. */
const CANDIDATE_MARGIN = 300;
/** Same screen radius `snap.ts` uses for its own rules. */
const SNAP_THRESHOLD_PX = 12;

/** Shapes a moving box spaces itself against; walls and zones are not
 *  part of a row. */
const isRowShape = (shape: Shape): boolean =>
  shape.type !== "wall" && shape.type !== "wall-path" && shape.type !== "zone";

const toBox = (rect: Rect): Box => ({
  left: rect.x,
  right: rect.x + rect.width,
  top: rect.y,
  bottom: rect.y + rect.height
});

export interface SnapMoveResult {
  delta: Point;
  guides: SnapGuides | null;
}

/**
 * Snapping for gestures: grid first, then the alignment rules from
 * `map-builder/utils/snap.ts` against candidates pulled from the spatial
 * index, so a drag never scans the whole document.
 */
export class SnapEngine {
  constructor(private readonly store: EditorStore) {}

  snapToGrid(value: number): number {
    const { ui, meta } = this.store.getState();
    if (!ui.snapToGrid) return value;
    return Math.round(value / meta.gridSize) * meta.gridSize;
  }

  snapPoint(point: Point): Point {
    return { x: this.snapToGrid(point.x), y: this.snapToGrid(point.y) };
  }

  /** Corrects a move delta for the given shapes; guides are for the overlay.
   *  With `showDistances` the gaps to the nearest neighbours come along. */
  snapMove(
    ids: string[],
    delta: Point,
    options: { showDistances?: boolean } = {}
  ): SnapMoveResult {
    const state = this.store.getState();
    const moving = ids
      .map((id) => state.shapes[id])
      .filter((shape): shape is Shape => !!shape);
    if (moving.length === 0) return { delta, guides: null };

    const primary = moving[0];
    let corrected = { ...delta };
    if (state.ui.snapToGrid) {
      corrected = {
        x: this.snapToGrid(primary.x + delta.x) - primary.x,
        y: this.snapToGrid(primary.y + delta.y) - primary.y
      };
    }
    if (!state.ui.showSnapLines) return { delta: corrected, guides: null };

    const movingBounds = unionRects(moving.map(getShapeAabb));
    if (!movingBounds) return { delta: corrected, guides: null };
    const searchArea: Rect = {
      x: movingBounds.x + corrected.x - CANDIDATE_MARGIN,
      y: movingBounds.y + corrected.y - CANDIDATE_MARGIN,
      width: movingBounds.width + CANDIDATE_MARGIN * 2,
      height: movingBounds.height + CANDIDATE_MARGIN * 2
    };
    const movingIds = new Set(ids);
    const candidates = state
      .getIndex()
      .shapesIn(searchArea, state.shapes)
      .filter(
        (shape) =>
          !movingIds.has(shape.id) &&
          !shape.locked &&
          (state.ui.showZones || shape.type !== "zone")
      );
    if (candidates.length === 0) return { delta: corrected, guides: null };

    const distancesFor = (bounds: Rect): SnapGuides["distances"] => {
      if (!options.showDistances) return undefined;
      return computeNeighborDistances(
        {
          left: bounds.x,
          right: bounds.x + bounds.width,
          top: bounds.y,
          bottom: bounds.y + bounds.height
        },
        candidates,
        movingIds
      );
    };

    if (moving.length === 1 && primary.type !== "wall-path") {
      const result = getSnapPosition(
        primary,
        candidates,
        { x: primary.x + corrected.x, y: primary.y + corrected.y },
        primary.id,
        state.viewport.scale
      );
      const finalDelta = {
        x:
          result.position.x !== undefined
            ? result.position.x - primary.x
            : corrected.x,
        y:
          result.position.y !== undefined
            ? result.position.y - primary.y
            : corrected.y
      };
      const refined = this.refine(
        movingBounds,
        corrected,
        finalDelta,
        result.lines,
        candidates
      );
      const distances = distancesFor({
        x: movingBounds.x + refined.delta.x,
        y: movingBounds.y + refined.delta.y,
        width: movingBounds.width,
        height: movingBounds.height
      });
      const allDistances = [...(distances ?? []), ...refined.distances];
      const hasGuide =
        refined.lines.x !== undefined ||
        refined.lines.y !== undefined ||
        allDistances.length > 0;
      return {
        delta: refined.delta,
        guides: hasGuide
          ? {
              ...refined.lines,
              targetDot: result.targetDot,
              centeredIn: result.centeredIn,
              activeBounds: result.activeBounds,
              distances: allDistances
            }
          : null
      };
    }

    const result = getGroupSnapPosition(
      {
        left: movingBounds.x + corrected.x,
        right: movingBounds.x + movingBounds.width + corrected.x,
        top: movingBounds.y + corrected.y,
        bottom: movingBounds.y + movingBounds.height + corrected.y
      },
      candidates,
      state.viewport.scale
    );
    const finalDelta = {
      x: corrected.x + (result.delta.x ?? 0),
      y: corrected.y + (result.delta.y ?? 0)
    };
    const refined = this.refine(
      movingBounds,
      corrected,
      finalDelta,
      result.lines,
      candidates
    );
    const distances = distancesFor({
      x: movingBounds.x + refined.delta.x,
      y: movingBounds.y + refined.delta.y,
      width: movingBounds.width,
      height: movingBounds.height
    });
    const allDistances = [...(distances ?? []), ...refined.distances];
    const hasGuide =
      refined.lines.x !== undefined ||
      refined.lines.y !== undefined ||
      allDistances.length > 0;
    return {
      delta: refined.delta,
      guides: hasGuide
        ? {
            ...refined.lines,
            targetDot: result.targetDot,
            distances: allDistances
          }
        : null
    };
  }

  /**
   * Per axis: a wall face within reach of the grid-snapped position wins
   * (touching a wall is the more deliberate placement, and the shared
   * rules would otherwise pull towards the wall's bounding box instead).
   * Where the shared edge/centre rules found nothing either, equal gaps
   * to the neighbours in the row.
   */
  private refine(
    movingBounds: Rect,
    gridDelta: Point,
    delta: Point,
    lines: { x?: number; y?: number },
    candidates: Shape[]
  ): {
    delta: Point;
    lines: { x?: number; y?: number };
    distances: NonNullable<SnapGuides["distances"]>;
  } {
    const threshold =
      SNAP_THRESHOLD_PX / Math.max(this.store.getState().viewport.scale, 0.1);
    const faces = candidates.flatMap(wallFaces);
    const rowBoxes = candidates
      .filter(isRowShape)
      .map((shape) => toBox(getShapeAabb(shape)));
    const placedAt = (at: Point): Box =>
      toBox({
        x: movingBounds.x + at.x,
        y: movingBounds.y + at.y,
        width: movingBounds.width,
        height: movingBounds.height
      });
    const next = { x: delta.x, y: delta.y };
    const nextLines = { ...lines };
    const distances: NonNullable<SnapGuides["distances"]> = [];
    for (const axis of ["x", "y"] as const) {
      const wall = snapToWallFaces(placedAt(gridDelta), faces, axis, threshold);
      if (wall) {
        next[axis] = gridDelta[axis] + wall.offset;
        nextLines[axis] = wall.guide;
        continue;
      }
      if (lines[axis] !== undefined) continue;
      const spacing = snapEqualSpacing(
        placedAt(next),
        rowBoxes,
        axis,
        threshold
      );
      if (spacing) {
        next[axis] += spacing.offset;
        distances.push(...spacing.distances);
      }
    }
    return { delta: next, lines: nextLines, distances };
  }
}
